import { t } from "./i18n";
/**
 * Federal Poverty Level (FPL) thresholds by household size.
 * Source: HHS Poverty Guidelines, contiguous US (Alaska/Hawaii use different tables).
 *
 * ----------------------------------------------------------------------------
 * ⚡ ANNUAL UPDATE — When HHS publishes new Poverty Guidelines (typically Jan):
 * ----------------------------------------------------------------------------
 * 1. Find the latest table at https://aspe.hhs.gov/topics/poverty-economic-mobility/poverty-guidelines
 * 2. Copy the 8 household-size values into a new `FPL_<YEAR>` object below.
 * 3. Update `FPL_ADDITIONAL` to the new "each additional person" value.
 * 4. Set `ACTIVE_FPL = FPL_<YEAR>` and `ACTIVE_FPL_ADDITIONAL` to match.
 * 5. Bump the "as of" comment on ACTIVE_FPL for auditability.
 * 6. No other file changes needed — fplBase/fplIncomeBands read ACTIVE_FPL.
 * ----------------------------------------------------------------------------
 */

// 2026 HHS Poverty Guidelines, contiguous 48 states + DC.
export const FPL_2026 = {
  1: 15960,
  2: 21640,
  3: 27320,
  4: 33000,
  5: 38680,
  6: 44360,
  7: 50040,
  8: 55720,
};
const FPL_2026_ADDITIONAL = 5680;

// TODO(2027): Paste the 2027 HHS table here when it is published, then flip
// ACTIVE_FPL below to FPL_2027. Example:
// export const FPL_2027 = { 1: 16250, 2: 22050, ... };
// const FPL_2027_ADDITIONAL = 5800;

// Active table — flip this single line each year.
const ACTIVE_FPL = FPL_2026; // as of 2026 HHS guidelines
const ACTIVE_FPL_ADDITIONAL = FPL_2026_ADDITIONAL;

export function fplBase(householdSize) {
  const n = Math.max(1, Number(householdSize) || 1);
  if (n <= 8) return ACTIVE_FPL[n];
  return ACTIVE_FPL[8] + (n - 8) * ACTIVE_FPL_ADDITIONAL;
}

/**
 * Returns income-band options keyed to the household size, with real $ ranges
 * shown next to each FPL percentage.
 */
export function fplIncomeBands(householdSize) {
  const base = fplBase(householdSize);
  const p = (pct) => Math.round(base * pct);
  const fmt = (n) => `$${n.toLocaleString()}`;
  return [
    { value: "at_or_below_100_fpl", label: `${t("At or below")} ${fmt(p(1.0))} (≤100% FPL)` },
    { value: "100_150_fpl",         label: `${fmt(p(1.0) + 1)}–${fmt(p(1.5))} (100–150% FPL)` },
    { value: "150_200_fpl",         label: `${fmt(p(1.5) + 1)}–${fmt(p(2.0))} (150–200% FPL)` },
    { value: "200_250_fpl",         label: `${fmt(p(2.0) + 1)}–${fmt(p(2.5))} (200–250% FPL)` },
    { value: "over_250_fpl",        label: `${t("Over")} ${fmt(p(2.5))} (>250% FPL)` },
  ];
}
